import React from "react";
import { Icon } from "@/components/ui-components/icon";
import "./cart-contents.css";
import { useNavigate } from "react-router-dom";
import type { DTOCartItemView } from "../../../../generated/api-client";
import type { LocaleCode } from "@/i18n/locales";
import type { InterpolationValues } from "@/i18n/types";
import type { Product } from "@/data/products";
import { calculateProductTotal } from "@/data/products";
import { formatCurrency } from "@/utils/format";
import { isPlaceholderImage, PLACEHOLDER_IMAGE_URL } from "@/utils/assets";
import { useAuthStore } from "@/app/state/auth-store";
import { PromotionCard, PromotionMotionSlot, PromotionProgressCard } from "@/components/app-components/product-promotion";

interface CartContentsProps {
    products: Product[];
    cart: Record<string, number>;
    cartItems: Record<string, DTOCartItemView>;
    pendingProductIds: Record<string, boolean>;
    error: string | null;
    locale: LocaleCode;
    subtotal: number;
    t: (key: string, values?: InterpolationValues) => string;
    onOpenProduct?: (product: Product | string) => void;
    onQuantityChange: (id: string, quantity: number) => Promise<boolean>;
    onRemove: (id: string) => Promise<boolean>;
}

const S3_BUCKET_URI = import.meta.env.VITE_S3_BUCKET_URI;

function serverImage(item: DTOCartItemView | undefined): string {
    return item?.imageFile ? `${S3_BUCKET_URI}/product-images/${item.imageFile}` : PLACEHOLDER_IMAGE_URL;
}

function isControlClick(target: EventTarget | null): boolean {
    return target instanceof Element && Boolean(target.closest("button,a,input,select,textarea"));
}

export function CartContents({
    products,
    cart,
    cartItems,
    pendingProductIds,
    error,
    locale,
    subtotal,
    t,
    onOpenProduct,
    onQuantityChange,
    onRemove
}: CartContentsProps) {
    const navigate = useNavigate();
    const authStatus = useAuthStore(state => state.status);
    const entries = Object.entries(cart);

    return (
        <>
            <div className="drawer-items">
                {!entries.length && <p className="cart-empty">{t("commerce.cart.empty")}</p>}
                {error && <p className="cart-error" role="alert">{t("commerce.cart.updateError")}</p>}
                {entries.map(([id, quantity]) => {
                    const product = products.find(item => item.id === id);
                    const serverItem = cartItems[id];
                    if (!product && !serverItem) return null;

                    const name = serverItem?.productName ?? product?.name ?? "";
                    const image = product?.image ?? serverImage(serverItem);
                    const regularUnitPrice = Number(serverItem?.onlinePrice ?? product?.price ?? 0);
                    const localPricing = product ? calculateProductTotal(product, quantity) : null;
                    const lineTotal = Number(serverItem?.lineTotal ?? localPricing?.total ?? regularUnitPrice * quantity);
                    const savings = Number(serverItem?.promotionSavings ?? localPricing?.savings ?? 0);
                    const appliedPromotion = serverItem?.appliedPromotion;
                    const isPending = Boolean(pendingProductIds[id]);
                    const openTarget = product ?? id;
                    const openProduct = () => onOpenProduct?.(openTarget);
                    const fixedProgress = serverItem?.promotions
                        .filter(promotion => promotion.isActive && promotion.discountType === "Fix" && promotion.minimumBuyQuantity > 0)
                        .map(promotion => {
                            const progress = Number((quantity % promotion.minimumBuyQuantity).toFixed(3));
                            return {
                                promotion,
                                progress,
                                unitsToAdd: Number((promotion.minimumBuyQuantity - progress).toFixed(3))
                            };
                        })
                        .filter(candidate => candidate.progress > 0)
                        .sort((left, right) => left.unitsToAdd - right.unitsToAdd)[0];

                    return (
                        <article
                            className={`cart-item${onOpenProduct ? " cart-item--interactive" : ""}`}
                            key={id}
                            role={onOpenProduct ? "button" : undefined}
                            tabIndex={onOpenProduct ? 0 : undefined}
                            onClick={event => {
                                if (!onOpenProduct || isControlClick(event.target)) return;
                                openProduct();
                            }}
                            onKeyDown={event => {
                                if (!onOpenProduct || isControlClick(event.target)) return;
                                if (event.key !== "Enter" && event.key !== " ") return;
                                event.preventDefault();
                                openProduct();
                            }}
                        >
                            <div className={`cart-img${isPlaceholderImage(image) ? " cart-img--placeholder" : ""}`}>
                                <img src={image} alt="" loading="lazy" />
                            </div>
                            <div>
                                <div className="cart-item__top">
                                    <strong>{name}</strong>
                                    <button
                                        className="remove"
                                        type="button"
                                        disabled={isPending}
                                        aria-label={t("commerce.cart.remove", { name })}
                                        onClick={() => void onRemove(id)}
                                    >
                                        <Icon name="x" size="sm" />
                                    </button>
                                </div>
                                <div className="cart-meta">
                                    {t("commerce.cart.unitPrice", { price: formatCurrency(regularUnitPrice, locale) })}
                                </div>

                                <PromotionMotionSlot visible={Boolean(appliedPromotion && savings > 0)}>
                                    {appliedPromotion && savings > 0 && (
                                        <PromotionCard
                                            promotion={appliedPromotion}
                                            quantity={quantity}
                                            regularUnitPrice={regularUnitPrice}
                                            lineTotal={lineTotal}
                                            savings={savings}
                                            locale={locale}
                                            t={t}
                                            className="cart-promotion cart-promotion--animated"
                                            style={{ viewTransitionName: `cart-promotion-${id}` }}
                                        />
                                    )}
                                </PromotionMotionSlot>

                                <PromotionMotionSlot visible={Boolean(fixedProgress)}>
                                    {fixedProgress && (
                                        <PromotionProgressCard
                                            promotion={fixedProgress.promotion}
                                            quantity={quantity}
                                            regularUnitPrice={regularUnitPrice}
                                            locale={locale}
                                            t={t}
                                            disabled={isPending}
                                            onAddUnits={units => void onQuantityChange(id, quantity + units)}
                                            className="cart-promotion-progress cart-promotion-progress--animated"
                                            style={{ viewTransitionName: `cart-progress-${id}` }}
                                        />
                                    )}
                                </PromotionMotionSlot>

                                <div className="cart-bottom">
                                    <div className="qty small" aria-label={name}>
                                        <button type="button" disabled={isPending} aria-label="-" onClick={() => void onQuantityChange(id, quantity - 1)}><Icon name="minus" size="sm" /></button>
                                        <span
                                            key={quantity}
                                            className="cart-animated-value"
                                            aria-live="polite"
                                            style={{ viewTransitionName: `cart-quantity-${id}` }}
                                        >
                                            {quantity}
                                        </span>
                                        <button type="button" disabled={isPending} aria-label="+" onClick={() => void onQuantityChange(id, quantity + 1)}><Icon name="plus" size="sm" /></button>
                                    </div>
                                    <div className="cart-line-total">
                                        <small>{t("commerce.cart.itemTotal")}</small>
                                        <span
                                            key={lineTotal}
                                            className="cart-price cart-animated-value"
                                            style={{ viewTransitionName: `cart-line-total-${id}` }}
                                        >
                                            {formatCurrency(lineTotal, locale)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </article>
                    );
                })}
            </div>
            <div className="drawer-summary">
                <div className="summary-row"><span>{t("commerce.cart.subtotal")}</span><strong key={`subtotal-${subtotal}`} className="cart-animated-value">{formatCurrency(subtotal, locale)}</strong></div>
                <div className="summary-row"><span>{t("commerce.cart.shipping")}</span><strong>{t("commerce.cart.shippingFree")}</strong></div>
                <div className="summary-row total"><span>{t("commerce.cart.total")}</span><strong key={`total-${subtotal}`} className="cart-animated-value">{formatCurrency(subtotal, locale)}</strong></div>
                <button
                    className="checkout-btn"
                    type="button"
                    disabled={!entries.length || authStatus === "checking"}
                    onClick={() => navigate(
                        authStatus === "authenticated" ? `/${locale}/payment` : `/${locale}/sign-in`,
                        authStatus === "authenticated" ? undefined : { state: { returnTo: `/${locale}/payment` } }
                    )}
                >
                    {t("commerce.cart.checkout")}
                </button>
            </div>
        </>
    );
}
